import { atom } from "@podium/store";
import type { PulseTracking } from "@m10s/search-frontend-clients/quest";
import { SemanticAd } from "src/types/SemanticAd";
import { SemanticFilter } from "src/types/SemanticFilter";

export type ErrorState = {
	hasError: boolean;
	errorType?: "client" | "server" | "network";
	statusCode?: number;
};

export type SearchStoreValue = {
	q?: string;
	docs?: SemanticAd[];
	filters?: SemanticFilter[];
	error?: ErrorState;
	inputValue?: string;
	resultsQuery?: string;
	hasTrackedResults?: boolean;
	tracking?: PulseTracking;
	isFiltersModalOpen?: boolean;
};

/**
 * Return the shared podium atom for search state.
 * Using a helper prevents typos in channel/topic across imports.
 */
export function getSearchStore() {
	return atom<SearchStoreValue>("recommerce", "search", {} as SearchStoreValue);
}

export function setSearchResults(
	results: SemanticAd[],
	q?: string,
	error?: ErrorState,
	filters?: SemanticFilter[],
	tracking?: PulseTracking,
) {
	const store = getSearchStore();
	const current = store.get?.() ?? {};
	const resolvedQuery = q ?? current.q ?? "";
	const nextInputValue = q ?? current.inputValue ?? resolvedQuery ?? "";
	const docs = !results || results.length === 0 ? [] : results;
	const resolvedFilters = Array.isArray(filters) ? filters : [];

	store.set({
		q: resolvedQuery,
		docs,
		filters: resolvedFilters,
		error,
		inputValue: nextInputValue,
		resultsQuery: resolvedQuery,
		hasTrackedResults: false,
		tracking,
	});
}

export function setSearchQuery(q: string) {
	const store = getSearchStore();
	const current = store.get?.() ?? {};
	const nextState: SearchStoreValue = {
		q,
		error: undefined,
		docs: current.docs,
		filters: current.filters,
		inputValue: q,
		resultsQuery: typeof current.resultsQuery !== "undefined" ? current.resultsQuery : undefined,
		hasTrackedResults: current.hasTrackedResults,
		tracking: current.tracking,
	};
	// Keep existing docs while loading new query to prevent page jump
	store.set(nextState);
}

export function setSearchError(q: string, error: ErrorState) {
	const store = getSearchStore();
	store.set({
		q,
		docs: [],
		filters: [],
		error,
		inputValue: q,
		resultsQuery: q,
		hasTrackedResults: true,
		tracking: undefined,
	});
}

export function clearSearchStore() {
	getSearchStore().set({ inputValue: "", tracking: undefined });
}

export function setSearchInputValue(inputValue: string) {
	const store = getSearchStore();
	const current = store.get?.() ?? {};
	store.set({
		...current,
		inputValue,
	});
}

export function markResultsTracked() {
	const store = getSearchStore();
	const current = store.get?.() ?? {};
	store.set({
		...current,
		hasTrackedResults: true,
	});
}

export function setFiltersModalOpen(isOpen: boolean) {
	const store = getSearchStore();
	const current = store.get?.() ?? {};
	store.set({
		...current,
		isFiltersModalOpen: isOpen,
	});
}
